import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import toast from "react-hot-toast";
import CommonFormLayout from "../../OptmisedCode/Form/CommonFormLayout";
import { MAIN_URL } from "../../../Configurations/Urls";

const BASE_ROUTE = "/organization/client";

const EMPTY_FORM = {
  client_name: "",
  client_short_name: "",
  client_code: "",
  website: "",
  email: "",
  phone: "",
  client_since: "",
  country_id: "",
  state_id: "",
  city_id: "",
  address: "",
  pincode: "",
  industry_id: "",
  status: "Active",
};

function authHeaders() {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function toDateInput(value) {
  if (!value || value === "-") return "";
  const s = String(value).slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "";
  return parsed.toISOString().slice(0, 10);
}

function optionalId(value) {
  if (value === "" || value == null) return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function emptyToNull(value) {
  if (value === "" || value == null) return null;
  return value;
}

export default function ClientForm({ mode = "add" }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = mode === "edit";
  const isView = mode === "view";
  const [loading, setLoading] = useState(isEdit || isView);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [industries, setIndustries] = useState([]);

  const setField = (key, value) => {
    setForm((prev) => {
      const next = { ...prev, [key]: value };
      if (key === "country_id") {
        next.state_id = "";
        next.city_id = "";
      }
      if (key === "state_id") {
        next.city_id = "";
      }
      return next;
    });
    setErrors((prev) => ({ ...prev, [key]: "" }));
  };

  useEffect(() => {
    let cancelled = false;
    const loadLookups = async () => {
      try {
        const [countryRes, industryRes] = await Promise.all([
          axios.get(`${MAIN_URL}/api/general/countries/v1`, {
            headers: authHeaders(),
          }),
          axios.get(`${MAIN_URL}/api/general/industry`, {
            headers: authHeaders(),
          }),
        ]);
        if (cancelled) return;
        setCountries(
          countryRes.data?.data ||
            countryRes.data?.countries ||
            countryRes.data ||
            []
        );
        setIndustries(
          industryRes.data?.industries || industryRes.data?.data || []
        );
      } catch {
        if (!cancelled) {
          setCountries([]);
          setIndustries([]);
        }
      }
    };
    loadLookups();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!form.country_id) {
      setStates([]);
      return;
    }
    let cancelled = false;
    axios
      .get(`${MAIN_URL}/api/general/countries/${form.country_id}/states`, {
        headers: authHeaders(),
      })
      .then((res) => {
        if (!cancelled) {
          setStates(res.data?.generalstates || res.data?.data || []);
        }
      })
      .catch(() => {
        if (!cancelled) setStates([]);
      });
    return () => {
      cancelled = true;
    };
  }, [form.country_id]);

  useEffect(() => {
    if (!form.country_id || !form.state_id) {
      setCities([]);
      return;
    }
    let cancelled = false;
    axios
      .get(
        `${MAIN_URL}/api/general/countries/${form.country_id}/states/${form.state_id}/cities`,
        { headers: authHeaders() }
      )
      .then((res) => {
        if (!cancelled) setCities(res.data?.data || res.data?.cities || []);
      })
      .catch(() => {
        if (!cancelled) setCities([]);
      });
    return () => {
      cancelled = true;
    };
  }, [form.country_id, form.state_id]);

  const loadClient = useCallback(async () => {
    if ((!isEdit && !isView) || !id) return;
    setLoading(true);
    try {
      const res = await axios.get(
        `${MAIN_URL}/api/client-management/clients/${id}`,
        { headers: authHeaders() }
      );
      const data = res.data?.data || res.data || {};
      setForm({
        client_name: data.client_name || "",
        client_short_name: data.client_short_name || "",
        client_code: data.client_code || "",
        website: data.website || "",
        email: data.email || "",
        phone: data.phone || data.phone_number || "",
        client_since: toDateInput(data.client_since),
        country_id: data.country_id ? String(data.country_id) : "",
        state_id: data.state_id ? String(data.state_id) : "",
        city_id: data.city_id ? String(data.city_id) : "",
        address: data.address || "",
        pincode: data.pincode || "",
        industry_id: data.industry_id ? String(data.industry_id) : "",
        status: data.status || "Active",
      });
    } catch (err) {
      toast.error(
        err.response?.data?.error ||
          err.response?.data?.message ||
          err.message ||
          "Failed to load client."
      );
    } finally {
      setLoading(false);
    }
  }, [id, isEdit, isView]);

  useEffect(() => {
    loadClient();
  }, [loadClient]);

  const validate = () => {
    const next = {};
    if (!form.client_name.trim()) next.client_name = "Client name is required.";
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      next.email = "Enter a valid email.";
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const payload = {
        client_name: form.client_name.trim(),
        client_short_name: emptyToNull(form.client_short_name.trim()),
        client_code: emptyToNull(form.client_code.trim()),
        website: emptyToNull(form.website.trim()),
        email: emptyToNull(form.email.trim()),
        phone: emptyToNull(form.phone.trim()),
        client_since: emptyToNull(form.client_since),
        country_id: optionalId(form.country_id),
        state_id: optionalId(form.state_id),
        city_id: optionalId(form.city_id),
        address: emptyToNull(form.address.trim()),
        pincode: emptyToNull(form.pincode.trim()),
        industry_id: optionalId(form.industry_id),
        status: form.status || "Active",
      };
      if (isEdit) {
        await axios.put(
          `${MAIN_URL}/api/client-management/clients/${id}`,
          payload,
          { headers: authHeaders() }
        );
        toast.success("Client updated.");
      } else {
        await axios.post(`${MAIN_URL}/api/client-management/clients`, payload, {
          headers: authHeaders(),
        });
        toast.success("Client added.");
      }
      navigate(BASE_ROUTE);
    } catch (err) {
      const apiErrors = err.response?.data?.errors || {};
      const mapped = {};
      Object.entries(apiErrors).forEach(([key, value]) => {
        mapped[key] = Array.isArray(value) ? value[0] : String(value);
      });
      if (Object.keys(mapped).length) setErrors(mapped);
      toast.error(
        mapped.client_name ||
          mapped.email ||
          err.response?.data?.error ||
          err.response?.data?.message ||
          err.message ||
          "Save failed."
      );
    } finally {
      setSaving(false);
    }
  };

  const fieldError = (key) =>
    errors[key] ? <span className="ems-fm-err">{errors[key]}</span> : null;

  return (
    <CommonFormLayout
      mode={mode}
      loading={loading}
      btnLoading={saving}
      onSubmit={handleSave}
      wrapInGrid={false}
      size="wide"
      headerProps={{
        addMessage: "Add client",
        updateMessage: "Edit client",
        homeLink: BASE_ROUTE,
        homeText: "Clients",
        editLink: id ? `${BASE_ROUTE}/edit/${id}` : undefined,
      }}
    >
      <div className="ems-fm-grid">
        <div className="ems-fm-field">
          <label>
            Client name {!isView && <span className="ems-fm-req">*</span>}
          </label>
          <input
            className="ems-fm-input"
            value={form.client_name}
            readOnly={isView}
            onChange={(e) => setField("client_name", e.target.value)}
            placeholder="Enter client name"
          />
          {fieldError("client_name")}
        </div>
        <div className="ems-fm-field">
          <label>Short name</label>
          <input
            className="ems-fm-input"
            value={form.client_short_name}
            readOnly={isView}
            onChange={(e) => setField("client_short_name", e.target.value)}
            placeholder="Short name"
          />
          {fieldError("client_short_name")}
        </div>
        <div className="ems-fm-field">
          <label>Client code</label>
          <input
            className="ems-fm-input"
            value={form.client_code}
            readOnly={isView}
            onChange={(e) => setField("client_code", e.target.value)}
            placeholder="Client code"
          />
          {fieldError("client_code")}
        </div>
        <div className="ems-fm-field">
          <label>Website</label>
          <input
            className="ems-fm-input"
            value={form.website}
            readOnly={isView}
            onChange={(e) => setField("website", e.target.value)}
            placeholder="https://"
          />
          {fieldError("website")}
        </div>
        <div className="ems-fm-field">
          <label>Email</label>
          <input
            className="ems-fm-input"
            type="text"
            inputMode="email"
            autoComplete="email"
            value={form.email}
            readOnly={isView}
            onChange={(e) => setField("email", e.target.value)}
            placeholder="name@company.com"
          />
          {fieldError("email")}
        </div>
        <div className="ems-fm-field">
          <label>Phone</label>
          <input
            className="ems-fm-input"
            value={form.phone}
            readOnly={isView}
            onChange={(e) => setField("phone", e.target.value)}
            placeholder="Phone number"
          />
          {fieldError("phone")}
        </div>
        <div className="ems-fm-field">
          <label>Client since</label>
          <input
            type="date"
            className="ems-fm-input"
            value={form.client_since}
            readOnly={isView}
            onChange={(e) => setField("client_since", e.target.value)}
          />
          {fieldError("client_since")}
        </div>
        <div className="ems-fm-field">
          <label>Status</label>
          <select
            className="ems-fm-input"
            value={form.status}
            disabled={isView}
            onChange={(e) => setField("status", e.target.value)}
          >
            <option value="Active">Active</option>
            <option value="Inactive">Inactive</option>
          </select>
        </div>
        <div className="ems-fm-field">
          <label>Industry</label>
          <select
            className="ems-fm-input"
            value={form.industry_id}
            disabled={isView}
            onChange={(e) => setField("industry_id", e.target.value)}
          >
            <option value="">Select industry</option>
            {industries.map((row) => (
              <option
                key={row.general_industry_id}
                value={String(row.general_industry_id)}
              >
                {row.industry_name}
              </option>
            ))}
          </select>
          {fieldError("industry_id")}
        </div>
        <div className="ems-fm-field">
          <label>Country</label>
          <select
            className="ems-fm-input"
            value={form.country_id}
            disabled={isView}
            onChange={(e) => setField("country_id", e.target.value)}
          >
            <option value="">Select country</option>
            {countries.map((row) => (
              <option
                key={row.general_country_id}
                value={String(row.general_country_id)}
              >
                {row.country_name}
              </option>
            ))}
          </select>
          {fieldError("country_id")}
        </div>
        <div className="ems-fm-field">
          <label>State</label>
          <select
            className="ems-fm-input"
            value={form.state_id}
            disabled={isView || !form.country_id}
            onChange={(e) => setField("state_id", e.target.value)}
          >
            <option value="">Select state</option>
            {states.map((row) => (
              <option
                key={row.general_state_id}
                value={String(row.general_state_id)}
              >
                {row.state_name}
              </option>
            ))}
          </select>
          {fieldError("state_id")}
        </div>
        <div className="ems-fm-field">
          <label>City</label>
          <select
            className="ems-fm-input"
            value={form.city_id}
            disabled={isView || !form.state_id}
            onChange={(e) => setField("city_id", e.target.value)}
          >
            <option value="">Select city</option>
            {cities.map((row) => (
              <option
                key={row.general_city_id}
                value={String(row.general_city_id)}
              >
                {row.city_name}
              </option>
            ))}
          </select>
          {fieldError("city_id")}
        </div>
        <div className="ems-fm-field">
          <label>Pincode</label>
          <input
            className="ems-fm-input"
            value={form.pincode}
            readOnly={isView}
            onChange={(e) => setField("pincode", e.target.value)}
            placeholder="Pincode"
          />
          {fieldError("pincode")}
        </div>
        <div className="ems-fm-field full">
          <label>Address</label>
          <textarea
            className="ems-fm-input ems-fm-textarea"
            value={form.address}
            readOnly={isView}
            onChange={(e) => setField("address", e.target.value)}
            placeholder="Street address"
            rows={3}
          />
          {fieldError("address")}
        </div>
      </div>
    </CommonFormLayout>
  );
}
